import { useId, useState } from 'react'
import { TickGlyph } from './TickMark'
import './choice.css'

/**
 * Picking from a small set of named options, with every option in view.
 *
 * The distinction from `Select` is what the reader has to do to compare: a
 * Select shows the answer and hides the alternatives, which is right for a long
 * or familiar list and wrong when the options differ in CONSEQUENCE. A choice
 * between "delete these too" and "measure them from something else" has to be
 * read before it is made, and a control that shows one of them at a time asks
 * somebody to hold the other in their head.
 *
 * One component, two modes, because they answer the same question with
 * different arity — and variants here are props, never new components.
 *
 * ## The shape carries the mode
 *
 * A radio is round and a checkbox is square, and that is the one place this
 * system softens a corner without the thing being a label ON content. It earns
 * it because the shape is the only thing that says whether picking a second
 * option REPLACES the first — a distinction people read before they read the
 * words, and the mode is otherwise invisible until somebody clicks twice and
 * watches the first answer disappear.
 *
 * ## The platform owns the semantics
 *
 * Same reasoning as `Toggle`: a hidden real input keeps this keyboard-operable
 * and announced correctly without any of it being re-implemented. A grouped
 * `type="radio"` already gives one tab stop for the set, arrow keys that move
 * AND select, and the right screen-reader wording. Hand-rolling that with
 * `aria-checked` and a roving `tabIndex` is a worse copy of what the platform
 * does, and it is the half that silently rots.
 *
 * The set is a `<fieldset>` and the question its `<legend>`, which is the
 * element built for exactly this. A div wearing `role="radiogroup"` and an
 * `aria-labelledby` is the same thing spelled out by hand, and it has to be
 * spelled right every time; a fieldset names its own group and disables every
 * control inside it from one attribute.
 */

export type ChoiceOption<T extends string = string> = {
  value: T
  label: string
  /** What picking it does — a sentence, where the label cannot carry it. */
  description?: string
  /**
   * How many the option would leave, where it narrows something.
   *
   * Set beside the label rather than inside it, so the number is a figure the
   * control renders and not a string every caller has to punctuate the same
   * way.
   */
  count?: number
  /**
   * What the option costs, set at the trailing edge in the figure face.
   *
   * A priced thing on a menu and a membership tier are the same control: both
   * are one answer out of a set, and both carry a figure the set is compared
   * on. A second component for the tier would be that component free to spell
   * a price differently from the menu two screens away.
   *
   * Formatted by the caller, because the money formatter is shared and the
   * decision about cents belongs where the figure is read.
   */
  amount?: string
  /** What the amount is per — `/month`, `each`. Absent where it is the price. */
  mark?: string
  disabled?: boolean
}

type Common<T extends string> = {
  /* Readonly, like `Select`'s: an options list written `as const` is what keeps
     the values literal, and that is what lets the chosen one keep its type. */
  options: readonly ChoiceOption<T>[]
  /**
   * The question, shown above the options and named as the group's label.
   *
   * Its own rather than a `Field`'s, because a Field points a `<label for>` at
   * ONE control and a choice is a set of them — the association would land on a
   * wrapper that cannot carry it. Here the prompt and the `aria-labelledby` are
   * the same string by construction, so they cannot come apart.
   */
  label?: string
  /** Names the set where no visible label is wanted. Ignored when `label` is set. */
  ariaLabel?: string
  /** This may be left unanswered — the same marker a `Field` carries, never
   *  a word typed into the question. */
  optional?: boolean
  /** Nothing here is yours to change; the state still reads. */
  disabled?: boolean
  /**
   * Lay the options across a row instead of down a column.
   *
   * For a SHORT set of short answers — is/is not, yes/no — where a column
   * spends three rows of height saying what fits on one. It stays a prop
   * rather than a second component, and it wraps rather than clipping, so a
   * narrow panel falls back to the column on its own.
   */
  inline?: boolean
  /**
   * How an option is drawn.
   *
   * `mark` is the default — a radio or a checkbox against a label, which is
   * what a question in a form looks like. `chip` draws each option as a
   * bordered target that fills with ink when it is chosen, for a set of PEERS
   * differing in one dimension and read as a row: a filter narrowing what is
   * beside it, four suggested amounts differing only in size. What rules it out
   * is an option that has to be weighed against the others — it needs a
   * sentence, and a sentence needs a mark.
   *
   * A prop rather than a second component, so the two appearances cannot come
   * apart on keyboard operation or on what a set is called.
   */
  appearance?: 'mark' | 'chip'
  /** What the set is about, read with its name — handed in by a `SettingBlock`. */
  'aria-describedby'?: string
}

type Single<T extends string> = Common<T> & {
  multiple?: false
  value?: T
  defaultValue?: T
  onValueChange?: (value: T) => void
}

type Multi<T extends string> = Common<T> & {
  multiple: true
  value?: T[]
  defaultValue?: T[]
  onValueChange?: (value: T[]) => void
}

/*
 * Generic over the value, the way `Select` is: a caller holding a union keeps
 * it, instead of casting `string` back to the type it just handed in. The
 * default keeps every call site that passes plain strings unchanged.
 */
export function Choice<T extends string = string>(props: Single<T> | Multi<T>) {
  const { options, label, ariaLabel, disabled, inline, appearance } = props
  const describedBy = props['aria-describedby']
  const chip = appearance === 'chip'
  /* Groups a set of radios in the DOM, which is what makes the browser treat
     them as one control rather than several. Never shown. */
  const name = useId()

  /*
   * Controlled and uncontrolled from one implementation: the prop wins where it
   * is given, and the internal state carries it where it is not. Written once
   * here so no call site has to choose between a `value` and a `defaultValue`
   * component.
   */
  const [held, setHeld] = useState<T[]>(() =>
    props.multiple
      ? (props.defaultValue ?? [])
      : props.defaultValue
        ? [props.defaultValue]
        : [],
  )
  /*
   * Controlled is decided by whether the PROP WAS PASSED, not by whether it
   * holds a value. A controlled set has to be able to say "nothing is chosen" —
   * a row of suggested amounts beside a field says exactly that the moment
   * somebody types a figure of their own — and reading an undefined value as
   * "uncontrolled" answers that with the last thing the set held, leaving a chip
   * marked while the field beside it says something else.
   */
  const controlled = 'value' in props
  const given = props.multiple
    ? props.value
    : props.value !== undefined
      ? [props.value]
      : []
  const chosen = new Set(controlled ? (given ?? []) : held)

  const pick = (value: T) => {
    if (props.multiple) {
      const next = new Set(chosen)
      // A checkbox toggles; a radio replaces. That IS the difference in arity.
      if (next.has(value)) next.delete(value)
      else next.add(value)
      const list = options.map((o) => o.value).filter((v) => next.has(v))
      if (!controlled) setHeld(list)
      props.onValueChange?.(list)
      return
    }
    if (!controlled) setHeld([value])
    props.onValueChange?.(value)
  }

  return (
    <fieldset
      className="choice"
      aria-describedby={describedBy}
      disabled={disabled}
      data-multiple={props.multiple ? '' : undefined}
      data-inline={inline ? '' : undefined}
      data-appearance={chip ? 'chip' : undefined}
    >
      {/* Always rendered, so the set always has a name: hidden from view where
          the surface around it already asks the question out loud. */}
      <legend className={label ? 'choice__prompt' : 'sr-only'}>
        {label ?? ariaLabel}
        {props.optional ? (
          <span className="field__optional">Optional</span>
        ) : null}
      </legend>
      {options.map((option) => {
        // `disabled` on the fieldset already reaches every input inside it;
        // this is the row's own dimming and the per-option case.
        const off = disabled || option.disabled
        return (
          <label
            key={option.value}
            className="choice__option"
            data-checked={chosen.has(option.value) ? '' : undefined}
            data-disabled={off ? '' : undefined}
          >
            <input
              className="sr-only"
              type={props.multiple ? 'checkbox' : 'radio'}
              name={name}
              value={option.value}
              checked={chosen.has(option.value)}
              disabled={off}
              onChange={() => pick(option.value)}
            />
            {/* A chip says what it is by filling with ink, so a mark beside
                the label would state the same thing twice. */}
            {chip ? null : (
              <TickGlyph
                checked={chosen.has(option.value)}
                round={!props.multiple}
              />
            )}
            <span className="choice__text">
              <span className="choice__label">
                {option.label}
                {/* A real space, so the option is ANNOUNCED as "Everyone 7"
                    rather than as one word; the gap either side of it is the
                    count's own margin. */}
                {option.count !== undefined && (
                  <>
                    {' '}
                    <span className="choice__count tabular">
                      {option.count}
                    </span>
                  </>
                )}
              </span>
              {option.description && (
                <span className="choice__description">
                  {option.description}
                </span>
              )}
            </span>
            {/* Trailing, so a column of options is a column of figures to
                compare rather than a figure buried in each label. */}
            {option.amount !== undefined && (
              <span className="choice__amount tabular">
                {option.amount}
                {option.mark !== undefined && (
                  <span className="choice__per">{option.mark}</span>
                )}
              </span>
            )}
          </label>
        )
      })}
    </fieldset>
  )
}
